import { Fragment, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { ApiError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { PageLoadingState } from '../../components/PageLoadingState';
import type { Item, ItemPatch } from '../../domain/inventory';
import { FULL_FLEET_ITEMS_QUERY_KEY } from '../dashboard/aggregations';

interface RowState {
  id: string;
  originalVersion: number;
  currentVersion: number | null;
  stale: boolean;
}

export interface BatchFieldChange {
  field: string;
  value: string;
}

export interface BatchChange {
  action: string;
  // A list rather than one field/value pair so a single review can cover
  // several fields at once -- see https://github.com/tidaltech/koi/issues/7796
  // (P1: combine status, attention, and location into one bulk edit instead
  // of three separate actions).
  fields: BatchFieldChange[];
  buildPatch: (item: Item) => Omit<ItemPatch, 'id' | 'version'>;
}

interface BatchUpdateDrawerProps {
  items: Item[];
  change: BatchChange;
  onClose: () => void;
  onApplied: () => void;
}

/**
 * One review surface for every atomic batch operation. At 200+ records the
 * surface expands into the full review workspace from ceremony 1f-C.
 */
export function BatchUpdateDrawer({ items, change, onClose, onApplied }: BatchUpdateDrawerProps) {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<RowState[]>(
    items.map((item) => ({
      id: item.id,
      originalVersion: item.version,
      currentVersion: null,
      stale: false,
    })),
  );
  const [checking, setChecking] = useState(true);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);
  const largeScope = items.length >= 200;

  useEffect(() => {
    let cancelled = false;
    setChecking(true);
    void Promise.all(items.map((item) => inventoryApi.getItem(item.id).catch(() => null))).then(
      (results) => {
        if (cancelled) return;
        setRows(
          items.map((item, index) => {
            const fresh = results[index];
            const currentVersion = fresh?.version ?? null;
            return {
              id: item.id,
              originalVersion: item.version,
              currentVersion,
              stale: currentVersion !== null && currentVersion !== item.version,
            };
          }),
        );
        setChecking(false);
      },
    );
    return () => {
      cancelled = true;
    };
    // The parent remounts this review for every new selection and change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      inventoryApi.batchUpdateItems(
        rows.map((row) => {
          const item = items.find((candidate) => candidate.id === row.id);
          if (!item) throw new Error(`Selected item ${row.id} is no longer in this review.`);
          return {
            id: row.id,
            version: row.currentVersion ?? row.originalVersion,
            ...change.buildPatch(item),
          };
        }),
      ),
    onSuccess: async () => {
      // See EditItemDrawer's onSuccess for why the full-fleet query is
      // invalidated separately with refetchType: 'none'.
      await queryClient.invalidateQueries({ queryKey: ['items'] });
      void queryClient.invalidateQueries({
        queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
        refetchType: 'none',
      });
      onApplied();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === 'NETWORK') {
        setUnknownOutcome(true);
        return;
      }
      setApplyError(
        error instanceof Error ? error.message : 'The batch update was rejected; no items changed.',
      );
    },
  });

  const staleCount = rows.filter((row) => row.stale).length;

  return (
    <div
      className={`drawer-overlay${largeScope ? ' batch-page-overlay' : ''}`}
      role="presentation"
      onClick={onClose}
    >
      <div
        className={largeScope ? 'drawer batch-review-page' : 'drawer'}
        role="dialog"
        aria-label="Review batch update"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="drawer-heading">
          <div>
            <span className="step-label">
              {largeScope ? 'High-impact scope · full review' : 'Atomic batch review'}
            </span>
            <h2>Review {items.length.toLocaleString()} changes</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close batch review"
          >
            ×
          </button>
        </div>

        {largeScope ? (
          <div className="warning-callout">
            This scope contains at least 200 records. Review the exact field and item list before
            applying the single atomic request.
          </div>
        ) : null}

        <dl className="batch-summary">
          <dt>Scope</dt>
          <dd>{items.length.toLocaleString()} selected items</dd>
          <dt>Action</dt>
          <dd>{change.action}</dd>
          {change.fields.map((field) => (
            <Fragment key={field.field}>
              <dt>{field.field}</dt>
              <dd>{field.value}</dd>
            </Fragment>
          ))}
        </dl>

        {applyError ? (
          <p role="alert" className="form-error">
            {applyError}
          </p>
        ) : null}
        {unknownOutcome ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({ queryKey: ['items'] });
              onClose();
            }}
          />
        ) : null}

        {checking ? (
          <PageLoadingState label="Checking current versions…" className="page-loading-inline" />
        ) : (
          <ul className="batch-item-list">
            {rows.map((row) => (
              <li
                key={row.id}
                className={row.stale ? 'batch-item-row batch-item-row-stale' : 'batch-item-row'}
              >
                <span className="mono">{row.id}</span>
                <span className="mono">v{row.currentVersion ?? row.originalVersion}</span>
                {row.stale ? <span className="batch-stale-tag">stale</span> : <span>ready</span>}
              </li>
            ))}
          </ul>
        )}

        <p className="batch-warning">
          {staleCount > 0
            ? `${staleCount} item${staleCount === 1 ? '' : 's'} changed since selection. Fresh versions will be included, but if any record fails validation or concurrency checks, no records change.`
            : 'Every current version is included. If any record fails validation or concurrency checks, no records change.'}
        </p>

        <div className="drawer-actions">
          <button type="button" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => {
              setApplyError(null);
              setUnknownOutcome(false);
              mutation.mutate();
            }}
            disabled={checking || mutation.isPending}
          >
            {mutation.isPending
              ? 'Applying atomically…'
              : `Apply to ${items.length.toLocaleString()} items`}
          </button>
        </div>
      </div>
    </div>
  );
}
